/**
 * Deck UI text overflow fallback policy.
 *
 * Numeric budgets are defined in slide JSON Schema `x-limits` (see `text-limits.ts`,
 * `slide-limits.ts`). This module states **how the UI must apply** those budgets so slides
 * do not invent one-off strategies (arbitrary wrap, font shrinking, silent clipping).
 *
 * ## 0. A static render draws complete text whole (wave D, owner rule)
 *
 * In a static render (the PDF and screenshot pass, `renderMode="static"`, set for a whole
 * page through `TextRenderModeContext`) `TextClamp` never clamps: no line clamp, no
 * single-line ellipsis, no whole-line fit. Text llmfill accepted over its box ships whole
 * and wraps; its overflow is the typed warning the pipeline already recorded
 * (`text_over_box`, `slide_content_overflow`). A host whose fixed box would clip the grown
 * text lets it grow in static (the exec cards, the rating-likert header block) or pays the
 * growth out of its own budget (the heat-grid headers and scale legend; the rating-likert
 * rows, which share one grid whose name column widens and whose rows grow into the
 * clip's spare room). Only cut text and
 * correctness errors fail a report. The interactive rules below are unchanged: there a
 * clamp stays, with the whole text on hover.
 *
 * ## 1. Prose / single-string fields (`x-limits.text`), interactive views
 *
 * | Case | Strategy | Deck UI implementation |
 * |------|----------|-------------------------|
 * | Titles, narrative, quotes, labels | **Line-clamp inside a fixed line box** | Pass {@link schemaBackedProseClampProps} into `TextClamp` (same numbers as {@link visibleTextClampFromSchema}). |
 * | `maxLines === 1` | **Single-line ellipsis** | `TextClamp`: `nowrap` + `text-overflow: ellipsis` (not multi-line wrap). |
 * | `maxLines > 1` | **Multi-line clamp** | `TextClamp`: `-webkit-line-clamp` (not unbounded height growth). |
 * | Clamped content whose full text still matters on hover | **Fast Radix tooltip (opt-in)** | `TextClamp`'s `tooltipOnTruncate` prop: per-instance `TooltipProvider` + `Tooltip delayDuration={300}`, mounted only once the clamp actually overflows (D4 detection). Sanctioned as the primary recovery path for matrix-rating row labels, measure headers, and value/average cells (docs/plans/deck-matrix-truncation-tooltip.md D3): no longer "rare defense-in-depth" for that slide. Replaces the native `title` attribute (~2s browser delay, unstylable) wherever it is used. |
 * | Rare defense-in-depth (unclamped strings with no `TextClamp` in the render path) | **Hard truncate** to `maxChars` | {@link proseTruncateToSchemaBudget} → `truncateToLimit`. |
 *
 * **Avoid** shrinking typography or adding unconstrained wrapping for schema-backed slots;
 * if content does not fit, prefer Python `enforce_deck_content_limits` and server-side chunking
 * over UI-only rescaling.
 *
 * **Schema caps raised for tooltip fidelity (D2/D6):** matrix-rating `title` maxChars 80→130
 * with `maxLines` 1→2, and matrix-rating row `label` maxLength 90→200. Rows show little more
 * visible text at 2 lines regardless of the higher cap: the payload preserves the full string
 * so the opt-in tooltip above has something complete to show on hover, not a second
 * server-side truncation the UI can't recover from. This does not change who owns
 * persistence-time truncation (still Python, §6); it only changes how much text the client is
 * handed to clamp and, on overflow, surface via tooltip. Same pattern applied for a long
 * open-end question title overrunning the base cap: base `title` maxLength 200→400, and
 * question-derived slides (qual-analysis, single-select-bars, multi-select-bars, rating-likert,
 * continuous-density, matrix-categorical-bars, ranking) gained an explicit `x-limits.text.title`
 * of 400 chars / 2 lines so the full question text survives into the payload for the hover
 * tooltip, even though only 2 lines render.
 *
 * ## 2. Repeatable rows / collections (`x-limits.items`)
 *
 * | Case | Strategy | Implementation |
 * |------|----------|----------------|
 * | Lists (concepts, rows, sidebar entries) | **Slice to schema count** | {@link schemaBackedItemSlice} → `clampArray`. |
 * | More items than one slide allows | **Generator chunking / extra slides** | Python; UI slice is defense-in-depth only. |
 *
 * **Do not** overlap or shrink list rows to fit extra items.
 *
 * ## 3. Chart primitives
 *
 * Cap density with `CHART_LIMITS` / `getChartLimit` in `slide-limits.ts` (rendering caps,
 * separate from `x-limits.text`).
 *
 * ## 4. Chrome without an `x-limits.text` key
 *
 * Use local numeric presets in `TEXT_CONSTRAINTS` (`text-constraints.ts`); still render via
 * `TextClamp` using the same line-clamp rules as §1.
 *
 * ## 5. Composed UI (multiple strings in one visual slot)
 *
 * You may spread {@link schemaBackedProseClampProps} and override `minLines` / `maxLines`
 * when layout requires a stricter subset than a single schema field; document inline (e.g.
 * qual-analysis theme attribution).
 *
 * ## 6. Cross-layer over-limit policy
 *
 * Chunking vs server truncate vs post-narrative sanitize vs UI clamp vs narrative-only trim
 * is a shared contract across Python and deck-ui layers. This file covers visible deck-ui
 * behavior only; Python owns persistence-time truncation and server-side chunking.
 */

import {
  getTextLimit,
  visibleTextClampFromSchema,
  truncateToLimit,
  type VisibleTextClampProps,
} from './text-limits'
import { clampArray } from './slide-limits'

/** Stable id for contract tests and audits. */
export const DECK_TEXT_OVERFLOW_POLICY_ID = 'CT-AUDIT-021E' as const

/** Cross-layer over-limit contract (chunk / truncate / warn / UI). */
export const DECK_OVER_LIMIT_POLICY_ID = 'CT-AUDIT-023D' as const

/** Stable id for schema-budget overrun detection (CT-AUDIT-023E); does not mutate payloads. */
export const DECK_VALIDATION_HOOKS_ID = 'CT-AUDIT-023E' as const

/**
 * CT-AUDIT-023E: client-side check that a string exceeds `x-limits.text` for `(field, slideType)`.
 * Use in tests, Storybook guards, or diagnostics — not a substitute for Python
 * `validate_slide_output` / `enforce_deck_content_limits`.
 */
export function payloadExceedsSchemaTextBudget(
  text: string | null | undefined,
  field: string,
  slideType: string
): boolean {
  if (text == null || text === '') {
    return false
  }
  return text.length > getTextLimit(field, slideType).maxChars
}

/**
 * Policy-compliant clamp props for schema-backed copy. Prefer this at slide call sites over
 * calling {@link visibleTextClampFromSchema} directly so overflow handling stays explicit.
 */
export function schemaBackedProseClampProps(
  field: string,
  slideType: string | undefined,
  presentation?: { lineHeight?: number | string }
): VisibleTextClampProps {
  return visibleTextClampFromSchema(field, slideType, presentation)
}

/** Hard truncate to schema `maxChars` (ellipsis). Use sparingly — see §1. */
export function proseTruncateToSchemaBudget(
  text: string | null | undefined,
  field: string,
  slideType?: string
): string {
  return truncateToLimit(text, field, slideType)
}

/** Slice a list to `x-limits.items` for `(slideType, field)`. See §2. */
export function schemaBackedItemSlice<T>(
  arr: T[] | null | undefined,
  slideType: string,
  field: string
): T[] {
  return clampArray(arr, slideType, field)
}

/** Type size, row gap and line budget for one column's insight bullets. */
export interface InsightBulletRhythm {
  fontSize: number
  gap: number
  maxLines: number
}

/**
 * Insight-bullet rhythm for the choice-family narrative column (single-select, multi-select,
 * ranking), keyed only by the typed bullet count, which the writer chooses (1-3).
 *
 * Three bullets keep the column's base rhythm. One or two would leave a band of empty column
 * between the copy and the quote card anchored at the bottom, so they take the column's
 * subtitle size (20px, already used one block above), a wider row gap and a third line. No
 * text is measured: the rule reads the count, and the existing `TextClamp` still bounds each
 * bullet.
 */
export function insightBulletRhythm(count: number): InsightBulletRhythm {
  if (count >= 3) {
    return { fontSize: 18, gap: 8, maxLines: 2 }
  }
  return { fontSize: 20, gap: 16, maxLines: 3 }
}
